import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useGoogleLogin } from '@react-oauth/google';
import { User, Mail, Lock, Shield } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { validatePassword } from '../utils/validation';
import { COUNTRY_CODES } from '../utils/constants';

export default function AuthPage() {
  const { login: onLogin, notify, isDark } = useAuth();
  const navigate = useNavigate();

  const [isLogin, setIsLogin] = useState(true);
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isForgot, setIsForgot] = useState(false);

  const [countryCode, setCountryCode] = useState("+91");
  const [phoneNum, setPhoneNum] = useState("");

  const [formData, setFormData] = useState({
    email: '', password: '', name: '', otp: '', new_password: ''
  });

  const particles = useMemo(() => Array.from({ length: 20 }).map((_, i) => ({
    id: i, size: Math.random() * 3 + 1, left: Math.random() * 100, top: Math.random() * 100,
    duration: Math.random() * 10 + 10, delay: Math.random() * 5
  })), []);

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        // Plain fetch (not the shared axios instance) so our own bearer token
        // never gets attached to a request bound for Google's servers.
        const userInfo = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
        }).then(r => r.json());

        const { email, name, picture } = userInfo;

        const res = await api.post(`/google-auth`, {
          email: email,
          name: name,
          avatar: picture
        });

        onLogin({ token: res.data.access_token, ...res.data.user });
        notify(`Welcome, ${name.split(' ')[0]}!`, "success");
        navigate('/');

      } catch (error) {
        console.error("Google Auth Error:", error);
        notify("Connection to Server Failed", "error");
      }
    },
    onError: () => notify("Google Login Failed", "error"),
  });

  const GoogleIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M23.52 12.29c0-.85-.08-1.68-.23-2.48H12v4.7h6.45c-.28 1.48-1.14 2.73-2.41 3.58v2.98h3.91c2.28-2.1 3.6-5.19 3.6-8.79z" fill="#4285F4" />
      <path d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.91-2.98c-1.08.72-2.45 1.15-4.02 1.15-3.13 0-5.77-2.11-6.72-4.95H1.36v3.12C3.34 21.3 8.36 24 12 24z" fill="#34A853" />
      <path d="M5.28 14.29c-.25-.73-.39-1.51-.39-2.29s.14-1.56.39-2.29V6.59H1.36C.49 8.33 0 10.1 0 12s.49 3.67 1.36 5.41l3.92-3.12z" fill="#FBBC05" />
      <path d="M12 4.75c1.76 0 3.34.6 4.58 1.79l3.44-3.44C17.95 1.13 15.24 0 12 0 8.36 0 3.34 2.7 1.36 6.59l3.92 3.12c.95-2.84 3.59-4.96 6.72-4.96z" fill="#EA4335" />
    </svg>
  );

  const currentRule = COUNTRY_CODES.find(c => c.code === countryCode);

  const validatePhone = () => {
    if (!currentRule) return true;
    if (phoneNum.length !== currentRule.len) return `Phone number for ${currentRule.name} must be ${currentRule.len} digits.`;
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      if (isForgot) {
        if (step === 1) {
          await api.post(`/forgot-password`, { email: formData.email.toLowerCase() });
          setStep(2); notify("Reset code sent!", "success");
        } else {
          const passError = validatePassword(formData.new_password);
          if (passError) { setError(passError); setIsLoading(false); return; }
          await api.post(`/reset-password`, { email: formData.email.toLowerCase(), otp: formData.otp, new_password: formData.new_password });
          notify("Password reset! Login now.", "success"); setIsForgot(false); setIsLogin(true); setStep(1);
        }
      } else if (isLogin) {
        const params = new URLSearchParams();
        params.append('username', formData.email.toLowerCase());
        params.append('password', formData.password);

        const res = await api.post(`/login`, params);
        onLogin({ token: res.data.access_token, ...res.data.user });
        notify("Welcome back!", "success");
        navigate('/');
      } else {
        if (step === 1) {
          const passError = validatePassword(formData.password);
          if (passError) { setError(passError); setIsLoading(false); return; }

          const phoneError = validatePhone();
          if (phoneError) { setError(phoneError); setIsLoading(false); return; }

          await api.post(`/register`, {
            email: formData.email.toLowerCase(),
            password: formData.password,
            phone: `${countryCode} ${phoneNum}`,
            name: formData.name
          });
          setStep(2);
          notify(`Code sent to ${formData.email}`, "success");
        } else {
          await api.post(`/verify`, { email: formData.email.toLowerCase(), otp: formData.otp });
          notify("Account verified!", "success"); setIsLogin(true); setStep(1);
        }
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Request failed";
      setError(msg);
      notify(msg, "error");
    }
    setIsLoading(false);
  };

  const toggleMode = () => { setIsLogin(!isLogin); setIsForgot(false); setStep(1); setError(''); };
  const toggleForgot = () => { setIsForgot(true); setIsLogin(false); setStep(1); setError(''); };

  return (
    <div className="ap-container">
      {/* ambient graphics layer */}
      <div className="ap-aurora ap-aurora-1" aria-hidden="true" />
      <div className="ap-aurora ap-aurora-2" aria-hidden="true" />
      <div className="ap-aurora ap-aurora-3" aria-hidden="true" />
      <div className="ap-grid" aria-hidden="true" />
      <div className="ap-vignette" aria-hidden="true" />

      {particles.map(p => (
        <motion.div
          key={p.id} className="ap-particle"
          style={{ width: p.size, height: p.size, left: `${p.left}%`, top: `${p.top}%` }}
          animate={{ y: [0, -30, 0], opacity: [0, 0.7, 0] }}
          transition={{ duration: p.duration, repeat: Infinity, delay: p.delay }}
        />
      ))}

      <motion.div className="ap-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="ap-card-sheen" aria-hidden="true" />

        <img src={isDark ? "/logo-txt.svg" : "/logo-light.svg"} alt="GigFlow" className="ap-logo" />

        <div className="ap-status" aria-hidden="true">
          <span className="ap-status-dot" />
          <span className="ap-status-track" />
        </div>

        <h2 className="ap-title">{isForgot ? (step === 1 ? 'Reset Password' : 'New Password') : (isLogin ? 'Welcome Back' : (step === 1 ? 'Create Account' : 'Verify'))}</h2>
        <p className="ap-subtitle">{isForgot ? "Secure Recovery" : (isLogin ? "Sign in to continue" : "Start your journey")}</p>

        {!isForgot && step === 1 && (
          <>
            <button className="ap-google" onClick={() => googleLogin()}>
              <GoogleIcon />
              <span>Continue with Google</span>
            </button>
            <div className="ap-divider"><span>OR</span></div>
          </>
        )}

        {error && <motion.div className="ap-error" initial={{ height: 0 }} animate={{ height: 'auto' }}>{error}</motion.div>}

        <form onSubmit={handleSubmit}>
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div key="step1" initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 10 }}>
                {!isLogin && !isForgot && <div className="ap-field"><label className="ap-label">Full Name</label><div className="ap-input-wrap"><User size={16} className="ap-input-icon" /><input className="ap-input" type="text" placeholder="Your full name" required value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} /></div></div>}

                <div className="ap-field">
                  <label className="ap-label">Email</label>
                  <div className="ap-input-wrap">
                    <Mail size={16} className="ap-input-icon" />
                    <input className="ap-input" type="email" placeholder="name@example.com" required value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value.toLowerCase() })} />
                  </div>
                </div>

                {!isForgot && <div className="ap-field"><label className="ap-label">Password</label><div className="ap-input-wrap"><Lock size={16} className="ap-input-icon" /><input className="ap-input" type="password" placeholder="••••••••" required value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} /></div></div>}

                {!isLogin && !isForgot && (
                  <div className="ap-field">
                    <label className="ap-label">Phone Number</label>
                    <div className="ap-phone-row">
                      <select className="ap-select" value={countryCode} onChange={e => setCountryCode(e.target.value)}>
                        {COUNTRY_CODES.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
                      </select>
                      <input
                        className="ap-input ap-phone"
                        type="tel"
                        placeholder={`${currentRule?.len || 10} digits`}
                        maxLength={currentRule?.len || 15}
                        value={phoneNum}
                        onChange={e => {
                          const val = e.target.value.replace(/\D/g, '');
                          if (val.length <= (currentRule?.len || 15)) setPhoneNum(val);
                        }}
                        required
                      />
                    </div>
                  </div>
                )}
              </motion.div>
            )}
            {step === 2 && (
              <motion.div key="step2" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }}>
                <div className="ap-field"><label className="ap-label">Code</label><div className="ap-input-wrap ap-otp-wrap"><Shield size={16} className="ap-input-icon" /><input className="ap-input ap-otp" type="text" placeholder="0000" maxLength="4" autoFocus style={{ textAlign: 'center', letterSpacing: '6px', fontWeight: 'bold' }} value={formData.otp} onChange={e => setFormData({ ...formData, otp: e.target.value })} /></div></div>
                {isForgot && <div className="ap-field"><label className="ap-label">New Password</label><div className="ap-input-wrap"><Lock size={16} className="ap-input-icon" /><input className="ap-input" type="password" placeholder="New Password" required value={formData.new_password} onChange={e => setFormData({ ...formData, new_password: e.target.value })} /></div></div>}
              </motion.div>
            )}
          </AnimatePresence>

          <button type="submit" className="ap-submit" disabled={isLoading}>
            {isLoading ? 'Processing...' : (isForgot ? (step === 1 ? 'Send Code' : 'Reset') : (isLogin ? 'Sign In' : (step === 1 ? 'Send Code' : 'Verify')))}
          </button>
        </form>
        <div className="ap-footer">
          {isForgot ? <span className="ap-link" onClick={toggleMode}>Cancel</span> : <><span className="ap-link" onClick={toggleMode}>{isLogin ? 'Create Account' : 'Log In'}</span>{isLogin && <div style={{ marginTop: 15 }}><span className="ap-link ap-link-quiet" onClick={toggleForgot} style={{ opacity: 0.7, fontSize: '0.8rem' }}>Forgot Password</span></div>}</>}
        </div>
      </motion.div>
    </div>
  );
}
